/* Encuesta de Capacitación: CRUD preguntas + dashboard de respuestas (Capacitador). */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import {
  ClipboardList, Plus, Edit3, Trash2, X, BarChart3,
  Star, MessageSquare, ListChecks, Type, AlignLeft, ToggleLeft, ToggleRight,
  CalendarClock, Building2,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPO_ICONS = { text: Type, long_text: AlignLeft, single_choice: ListChecks, scale: Star };
const TIPO_LABELS = { text: 'Texto corto', long_text: 'Texto largo', single_choice: 'Opción única', scale: 'Escala 1-5' };

// Detección de campos "smart": la encuesta pública los auto-llena o los convierte
// en selector especial. Reflejamos esa lógica aquí para que el capacitador vea
// el badge correcto en lugar de "Texto corto".
function smartFieldMeta(p) {
  const lower = (p.texto || '').toLowerCase();
  const isTextish = p.tipo === 'text' || p.tipo === 'long_text';
  if (isTextish && lower.includes('fecha')) {
    return {
      label: 'Fecha automática (Chile)',
      icon: CalendarClock,
      classes: 'text-teal-700 bg-teal-50 border-teal-200',
    };
  }
  if (isTextish && lower.includes('empresa')) {
    return {
      label: 'Selector de empresas',
      icon: Building2,
      classes: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    };
  }
  return null;
}

export default function CapacitadorEncuesta() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState('preguntas');
  const [preguntas, setPreguntas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalPreg, setModalPreg] = useState(null); // {mode, pregunta?}

  async function cargar() {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/encuesta/preguntas`, { headers });
      setPreguntas(r.data);
    } finally { setLoading(false); }
  }
  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  async function toggleActiva(p) {
    try {
      await axios.put(`${API}/encuesta/preguntas/${p.id}`, { activa: !p.activa }, { headers });
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  }

  async function eliminar(p) {
    if (!window.confirm(`¿Eliminar la pregunta "${p.texto}"?\n\nLas respuestas históricas se mantienen pero la pregunta no aparecerá más.`)) return;
    try {
      await axios.delete(`${API}/encuesta/preguntas/${p.id}`, { headers });
      toast.success('Pregunta eliminada');
      cargar();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
  }

  return (
    <div className="px-6 py-6 max-w-5xl mx-auto" data-testid="encuesta-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-violet-50 border border-violet-200 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-violet-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Encuesta de Evaluación</h1>
            <p className="text-sm text-slate-500">El trabajador la responde ANTES de rendir la prueba.</p>
          </div>
        </div>
        {tab === 'preguntas' && (
          <Button onClick={() => setModalPreg({ mode: 'create' })} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="nueva-pregunta-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Nueva Pregunta
          </Button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 mb-5">
        <button onClick={() => setTab('preguntas')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === 'preguntas' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-preguntas">
          <ListChecks className="inline w-4 h-4 mr-1.5" /> Preguntas ({preguntas.length})
        </button>
        <button onClick={() => setTab('dashboard')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${tab === 'dashboard' ? 'border-violet-600 text-violet-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
          data-testid="tab-dashboard">
          <BarChart3 className="inline w-4 h-4 mr-1.5" /> Dashboard de Respuestas
        </button>
      </div>

      {tab === 'preguntas' && (
        <PreguntasTab preguntas={preguntas} loading={loading}
          onEdit={(p) => setModalPreg({ mode: 'edit', pregunta: p })}
          onToggle={toggleActiva} onDelete={eliminar} />
      )}
      {tab === 'dashboard' && <DashboardTab headers={headers} />}

      {modalPreg && (
        <ModalPregunta modal={modalPreg} onClose={() => setModalPreg(null)}
          onSaved={() => { setModalPreg(null); cargar(); }} headers={headers} maxOrden={preguntas.length} />
      )}
    </div>
  );
}


function PreguntasTab({ preguntas, loading, onEdit, onToggle, onDelete }) {
  if (loading) return <p className="py-14 text-center text-sm text-slate-400">Cargando...</p>;
  if (preguntas.length === 0) return <p className="py-14 text-center text-sm text-slate-400">No hay preguntas. Agrega la primera.</p>;
  return (
    <ul className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 shadow-sm">
      {preguntas.map((p) => {
        const smart = smartFieldMeta(p);
        const Icon = smart ? smart.icon : (TIPO_ICONS[p.tipo] || Type);
        return (
          <li key={p.id} className="px-5 py-3.5 flex items-start gap-3" data-testid={`pregunta-${p.id}`}>
            <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600 shrink-0">
              {p.orden}
            </div>
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${p.activa ? 'text-slate-900' : 'text-slate-400 line-through'}`}>
                {p.texto}
              </p>
              <div className="flex items-center gap-2 mt-1 text-[11px] flex-wrap">
                <span className={`inline-flex items-center gap-1 border px-1.5 py-0.5 rounded ${smart ? smart.classes : 'text-violet-700 bg-violet-50 border-violet-200'}`}>
                  <Icon className="w-3 h-3" /> {smart ? smart.label : (TIPO_LABELS[p.tipo] || p.tipo)}
                </span>
                {p.requerida && (
                  <span className="text-rose-700 bg-rose-50 border border-rose-200 px-1.5 py-0.5 rounded">Obligatoria</span>
                )}
                {p.tipo === 'single_choice' && (p.opciones || []).length > 0 && (
                  <span className="text-slate-500 italic">{p.opciones.join(' · ')}</span>
                )}
              </div>
            </div>
            <button onClick={() => onToggle(p)}
              className="p-1.5 hover:bg-slate-100 rounded text-slate-500" title={p.activa ? 'Desactivar' : 'Activar'}
              data-testid={`toggle-${p.id}`}>
              {p.activa ? <ToggleRight className="w-5 h-5 text-emerald-600" /> : <ToggleLeft className="w-5 h-5 text-slate-300" />}
            </button>
            <button onClick={() => onEdit(p)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Editar"
              data-testid={`edit-${p.id}`}>
              <Edit3 className="w-4 h-4" />
            </button>
            <button onClick={() => onDelete(p)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600" title="Eliminar"
              data-testid={`delete-${p.id}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        );
      })}
    </ul>
  );
}


function DashboardTab({ headers }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/encuesta/dashboard`, { headers }).then(r => setData(r.data)).finally(() => setLoading(false));
  // eslint-disable-next-line
  }, []);

  if (loading) return <p className="py-14 text-center text-sm text-slate-400">Cargando dashboard...</p>;
  if (!data || data.total_respuestas === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center">
        <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-2" />
        <p className="text-sm text-slate-500">Aún no hay respuestas registradas.</p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiBox label="Respuestas recibidas" value={data.total_respuestas} color="violet" />
        <KpiBox label="Promedio global (escalas 1-5)" value={data.promedio_global_scale ?? '—'} suffix=" / 5" color="emerald" />
        <KpiBox label="Preguntas en formulario" value={data.preguntas.length} color="slate" />
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 shadow-sm">
        {data.preguntas.map(p => (
          <div key={p.id} className="px-5 py-4" data-testid={`dash-pregunta-${p.id}`}>
            <p className="text-sm font-semibold text-slate-900 mb-2">[{p.orden}] {p.texto}</p>
            <p className="text-[11px] text-slate-400 mb-2">{p.n_respuestas} respuestas · {(smartFieldMeta(p)?.label) || TIPO_LABELS[p.tipo] || p.tipo}</p>
            {p.tipo === 'scale' && p.promedio !== null && p.promedio !== undefined && (
              <ScaleChart promedio={p.promedio} distribucion={p.distribucion} total={p.n_respuestas} />
            )}
            {p.tipo === 'single_choice' && (
              <ChoiceChart distribucion={p.distribucion} total={p.n_respuestas} opciones={p.opciones} />
            )}
            {(p.tipo === 'text' || p.tipo === 'long_text') && (p.comentarios || []).length > 0 && (
              <details>
                <summary className="cursor-pointer text-xs text-blue-600 hover:underline">Ver {p.comentarios.length} respuestas</summary>
                <ul className="mt-2 space-y-1 max-h-48 overflow-y-auto text-xs text-slate-700">
                  {p.comentarios.map((c, i) => <li key={i} className="border-l-2 border-slate-200 pl-2">{c}</li>)}
                </ul>
              </details>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function KpiBox({ label, value, suffix = '', color }) {
  const colors = { violet: 'text-violet-700', emerald: 'text-emerald-700', slate: 'text-slate-900' }[color];
  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
      <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</p>
      <p className={`text-2xl font-black tabular-nums ${colors}`}>{value}{suffix}</p>
    </div>
  );
}

function ScaleChart({ promedio, distribucion, total }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
        <span className="text-xl font-black text-slate-900 tabular-nums">{promedio}</span>
        <span className="text-xs text-slate-400">/ 5</span>
      </div>
      <div className="grid grid-cols-5 gap-1">
        {['1', '2', '3', '4', '5'].map(k => {
          const n = distribucion[k] || 0;
          const pct = total ? Math.round((n / total) * 100) : 0;
          return (
            <div key={k} className="text-center">
              <div className="bg-slate-100 rounded h-16 flex items-end overflow-hidden">
                <div className="w-full bg-violet-500 transition-all" style={{ height: `${pct}%` }} />
              </div>
              <p className="text-[10px] text-slate-500 mt-0.5">{k}</p>
              <p className="text-[10px] font-semibold text-slate-700">{n}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ChoiceChart({ distribucion, total, opciones }) {
  const entries = Object.entries(distribucion || {});
  return (
    <ul className="space-y-1.5">
      {(opciones && opciones.length > 0 ? opciones : entries.map(([k]) => k)).map(opt => {
        const n = (distribucion || {})[opt] || 0;
        const pct = total ? Math.round((n / total) * 100) : 0;
        return (
          <li key={opt}>
            <div className="flex items-center justify-between text-xs text-slate-700 mb-0.5">
              <span>{opt}</span>
              <span className="font-semibold tabular-nums">{n} ({pct}%)</span>
            </div>
            <div className="bg-slate-100 rounded-full h-2 overflow-hidden">
              <div className="h-full bg-violet-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}


function ModalPregunta({ modal, onClose, onSaved, headers, maxOrden }) {
  const isEdit = modal.mode === 'edit';
  const p = modal.pregunta;
  const [form, setForm] = useState({
    texto: p?.texto || '',
    tipo: p?.tipo || 'scale',
    opciones: (p?.opciones || []).join('\n'),
    requerida: p ? p.requerida : true,
    activa: p ? p.activa : true,
    orden: p?.orden || maxOrden + 1,
  });
  const [saving, setSaving] = useState(false);

  async function submit(ev) {
    ev.preventDefault();
    if (!form.texto.trim()) { toast.error('El texto es obligatorio'); return; }
    const body = {
      texto: form.texto.trim(),
      tipo: form.tipo,
      requerida: !!form.requerida,
      orden: parseInt(form.orden, 10) || maxOrden + 1,
    };
    if (form.tipo === 'single_choice') {
      const opts = form.opciones.split('\n').map(s => s.trim()).filter(Boolean);
      if (opts.length < 2) { toast.error('Mínimo 2 opciones'); return; }
      body.opciones = opts;
    } else {
      body.opciones = null;
    }
    if (isEdit) body.activa = !!form.activa;
    setSaving(true);
    try {
      if (isEdit) {
        await axios.put(`${API}/encuesta/preguntas/${p.id}`, body, { headers });
        toast.success('Pregunta actualizada');
      } else {
        await axios.post(`${API}/encuesta/preguntas`, body, { headers });
        toast.success('Pregunta creada');
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg my-8" data-testid="modal-pregunta">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">{isEdit ? 'Editar Pregunta' : 'Nueva Pregunta'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded text-slate-400"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Texto de la pregunta *</label>
            <textarea value={form.texto} onChange={e => setForm({ ...form, texto: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm min-h-[72px]"
              data-testid="form-texto" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Tipo *</label>
              <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white"
                data-testid="form-tipo">
                <option value="scale">Escala 1-5</option>
                <option value="single_choice">Opción única</option>
                <option value="text">Texto corto</option>
                <option value="long_text">Texto largo</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Orden</label>
              <Input type="number" value={form.orden} onChange={e => setForm({ ...form, orden: e.target.value })} />
            </div>
          </div>
          {form.tipo === 'single_choice' && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Opciones (una por línea)</label>
              <textarea value={form.opciones} onChange={e => setForm({ ...form, opciones: e.target.value })}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm min-h-[72px] font-mono"
                placeholder={"Opción 1\nOpción 2\nOpción 3"}
                data-testid="form-opciones" />
            </div>
          )}
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={form.requerida} onChange={e => setForm({ ...form, requerida: e.target.checked })} />
              <span className="text-slate-700">Obligatoria</span>
            </label>
            {isEdit && (
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={form.activa} onChange={e => setForm({ ...form, activa: e.target.checked })} />
                <span className="text-slate-700">Activa</span>
              </label>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
            <Button type="button" variant="ghost" onClick={onClose} disabled={saving}>Cancelar</Button>
            <Button type="submit" disabled={saving} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="form-guardar">
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
